import { FiTrendingUp } from 'react-icons/fi';

export const describingTrendsData = {
  courseId: 'describing-trends',
  courseType: 'writing-course',
  title: 'Describing Trends',
  icon: FiTrendingUp,
  overview: {
    impact: 'Describe data movements precisely in Task 1',
    focus: 'Trend vocabulary, adjective-adverb pairs, overview writing',
    uses: 'Writing Task 1 (line graphs, bar charts, tables)',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Vocabulary for Upward and Downward Trends',
      content: `<p>IELTS Task 1 requires you to describe changes in data using <strong>precise vocabulary</strong>. Using varied language for increases and decreases is key to a high Lexical Resource score.</p>
      <p>Each trend word can function as a <strong>verb</strong> or a <strong>noun</strong>:</p>`,
      table: {
        headers: ['Direction', 'Verbs', 'Nouns'],
        rows: [
          ['Strong increase', 'surge, soar, rocket, skyrocket', 'a surge, a sharp rise'],
          ['Moderate increase', 'rise, grow, increase, climb', 'a rise, an increase, growth'],
          ['Slight increase', 'edge up, creep up, inch up', 'a slight rise, a marginal increase'],
          ['Strong decrease', 'plummet, plunge, crash, slump', 'a plunge, a sharp fall'],
          ['Moderate decrease', 'fall, drop, decline, decrease', 'a fall, a decline, a drop'],
          ['Slight decrease', 'dip, edge down, slip', 'a slight dip, a minor decline'],
        ],
      },
      points: [
        'Use verbs in the past tense for completed data: "Sales <strong>rose</strong> from 100 to 200"',
        'Use nouns with "there was": "There was a <strong>sharp increase</strong> in sales"',
        'Avoid repeating the same word — vary between "rose", "climbed", "increased"',
      ],
      warning: 'Do not use emotional language like "amazing increase" or "terrible drop". Keep descriptions objective and academic.',
    },
    {
      title: 'Adjectives and Adverbs for Degree of Change',
      content: `<p>Pair your trend words with <strong>adjectives</strong> (before nouns) or <strong>adverbs</strong> (before verbs) to show how large or small the change was.</p>`,
      table: {
        headers: ['Degree', 'Adjectives (+ noun)', 'Adverbs (+ verb)'],
        rows: [
          ['Very large', 'dramatic, sharp, steep, enormous', 'dramatically, sharply, steeply'],
          ['Large', 'significant, substantial, considerable', 'significantly, substantially, considerably'],
          ['Moderate', 'moderate, steady, gradual', 'moderately, steadily, gradually'],
          ['Small', 'slight, minor, marginal', 'slightly, marginally'],
        ],
      },
      points: [
        'Adjective + noun: "a <strong>significant</strong> increase", "a <strong>slight</strong> decline"',
        'Adverb + verb: "Sales <strong>rose sharply</strong>", "The number <strong>fell slightly</strong>"',
        'Match the degree to the actual data — do not describe a 2% change as "dramatic"',
        '<strong>Steady</strong> and <strong>gradual</strong> both suggest consistent change over time, not sudden spikes',
      ],
      examples: [
        {
          label: 'Adjective + noun pattern',
          text: 'There was a gradual increase in the number of university graduates between 2000 and 2015.',
        },
        {
          label: 'Adverb + verb pattern',
          text: 'The percentage of internet users rose dramatically from 10% in 2000 to 85% in 2020.',
        },
      ],
    },
    {
      title: 'Describing Stability and Fluctuation',
      content: `<p>Not all data changes. You also need vocabulary for periods where data <strong>stays the same</strong> or <strong>moves up and down irregularly</strong>.</p>`,
      table: {
        headers: ['Pattern', 'Verbs', 'Nouns / Phrases'],
        rows: [
          ['Stability', 'remain steady, stay constant, level off, plateau, stabilise', 'a period of stability, no significant change'],
          ['Fluctuation', 'fluctuate, vary, oscillate', 'fluctuation, variation'],
          ['Peak', 'peak, reach a peak, hit a high', 'a peak of, the highest point'],
          ['Low point', 'bottom out, reach a low, hit a trough', 'a low of, the lowest point'],
        ],
      },
      points: [
        '"Level off" means data stops rising/falling and becomes flat — "After 2015, growth <strong>levelled off</strong> at around 60%"',
        '"Fluctuate" means repeated irregular rises and falls — "Prices <strong>fluctuated</strong> between $30 and $50"',
        'Always state the value when mentioning peaks and lows: "reaching a <strong>peak of 85%</strong> in 2018"',
        'Use "approximately", "around", or "roughly" when citing round numbers',
      ],
      examples: [
        {
          label: 'Stability',
          text: 'After the initial growth period, the figure remained relatively stable at approximately 40% for the next five years.',
        },
        {
          label: 'Fluctuation and peak',
          text: 'Oil prices fluctuated throughout the decade, peaking at $120 per barrel in 2008 before falling sharply.',
        },
      ],
    },
    {
      title: 'Writing an Overview for Task 1',
      content: `<p>The <strong>overview</strong> is the most important paragraph in your Task 1 response. It summarises the main trends or key features <strong>without citing specific data</strong>.</p>
      <p>Examiners look for this paragraph when awarding the Task Achievement score.</p>`,
      points: [
        'Place the overview after your introduction (second paragraph) or at the end',
        'Identify 2-3 main trends or patterns — do not describe every detail',
        'Use phrases like "Overall,", "In general,", "The most notable trend is…"',
        'Do <strong>not</strong> include specific numbers in the overview — save those for body paragraphs',
        'Compare the start and end of the data period, or highlight the biggest change',
      ],
      examples: [
        {
          label: 'Good overview (line graph)',
          text: 'Overall, the number of tourists visiting both countries increased over the period, although Country A experienced much more rapid growth than Country B.',
        },
        {
          label: 'Good overview (bar chart)',
          text: 'In general, spending on education was highest in all three years, while expenditure on defence remained the lowest throughout the period.',
        },
        {
          label: 'Poor overview (too detailed)',
          text: 'Overall, tourists to Country A rose from 1.2 million in 2010 to 4.8 million in 2020, a rise of 300%, while Country B went from 800,000 to 1.5 million.',
        },
      ],
      warning: 'A missing overview can limit your Task Achievement score to Band 5. Always include one.',
    },
  ],
  exercises: [
    {
      title: 'Match Trend Words to Directions',
      type: 'select',
      instructions: 'What direction does each word describe?',
      questions: [
        {
          text: '"plummeted"',
          options: ['Sharp decrease', 'Sharp increase', 'Stability', 'Fluctuation'],
          answer: 'Sharp decrease',
          explanation: '"Plummeted" means fell very quickly and dramatically.',
        },
        {
          text: '"edged up"',
          options: ['Slight increase', 'Sharp increase', 'Slight decrease', 'Stability'],
          answer: 'Slight increase',
          explanation: '"Edged up" means increased by a very small amount.',
        },
        {
          text: '"levelled off"',
          options: ['Stability', 'Sharp decrease', 'Fluctuation', 'Moderate increase'],
          answer: 'Stability',
          explanation: '"Levelled off" means the data stopped changing and remained flat.',
        },
        {
          text: '"surged"',
          options: ['Sharp increase', 'Sharp decrease', 'Stability', 'Slight increase'],
          answer: 'Sharp increase',
          explanation: '"Surged" means increased suddenly and dramatically.',
        },
      ],
    },
    {
      title: 'Describe Changes Correctly',
      type: 'fill-in',
      instructions: 'Complete each sentence with the correct adjective or adverb to match the data described.',
      questions: [
        {
          text: 'Sales went from $10 million to $10.5 million — a ___ increase. (small change)',
          answer: 'slight',
          explanation: 'A 5% change is small, so "slight" (or "marginal") is appropriate. "Dramatic" would be inaccurate.',
        },
        {
          text: 'The population rose ___ from 2 million to 8 million in just five years. (very large, fast change)',
          answer: 'dramatically',
          explanation: 'Quadrupling in five years is a very large, fast change, so the adverb "dramatically" fits.',
        },
        {
          text: 'There was a ___ decline in newspaper readership over the 20-year period. (consistent, not sudden)',
          answer: 'gradual',
          explanation: 'A decline spread over 20 years is consistent and steady, so "gradual" is the right adjective.',
        },
        {
          text: 'Unemployment ___ between 5% and 12% during the decade. (up and down irregularly)',
          answer: 'fluctuated',
          explanation: 'Irregular movement between two values is described by "fluctuated".',
        },
      ],
    },
    {
      title: 'Choose the Correct Description',
      type: 'select',
      instructions: 'Which sentence most accurately describes the data?',
      questions: [
        {
          text: 'Data: Website visitors went from 1,000 in January to 50,000 in December.',
          options: [
            'The number of visitors rose dramatically over the year, reaching 50,000 by December.',
            'The number of visitors increased slightly from January to December.',
            'Visitor numbers remained stable throughout the year.',
            'There was a gradual decline in website visitors.',
          ],
          answer: 'The number of visitors rose dramatically over the year, reaching 50,000 by December.',
          explanation: 'A 50-fold increase is dramatic. "Slightly" would be inaccurate, and the data clearly shows an increase, not stability or decline.',
        },
        {
          text: 'Data: Temperature stayed between 20°C and 22°C for six months.',
          options: [
            'The temperature remained relatively stable at around 21°C.',
            'The temperature rose sharply over the six-month period.',
            'There was a dramatic fluctuation in temperature.',
            'The temperature plummeted during this time.',
          ],
          answer: 'The temperature remained relatively stable at around 21°C.',
          explanation: 'A 2-degree range over six months indicates stability. The variation is too small to call a fluctuation or a rise.',
        },
        {
          text: 'Data: Exports fell from $500M to $480M, then rose to $510M, then fell to $490M.',
          options: [
            'Export values fluctuated between approximately $480M and $510M.',
            'Exports declined sharply over the period.',
            'There was a steady increase in exports.',
            'Exports surged to a peak of $510M.',
          ],
          answer: 'Export values fluctuated between approximately $480M and $510M.',
          explanation: 'The repeated rises and falls within a narrow range describe a fluctuation pattern.',
        },
      ],
    },
    {
      title: 'Write Overview Sentences',
      type: 'writing',
      instructions: 'Write an overview sentence for each scenario. Do not include specific numbers.',
      questions: [
        {
          text: 'Line graph: Car sales increased steadily from 2000-2010, then decreased sharply from 2010-2020. Bicycle sales grew throughout the entire period.',
          example: 'Overall, while car sales initially rose before experiencing a significant decline, bicycle sales showed consistent growth over the 20-year period.',
          tip: 'Identify the two main trends and contrast them. Use "while" or "whereas" to compare.',
        },
        {
          text: 'Bar chart: Country A spends the most on healthcare in all years. Country C spends the least. Country B is in the middle but growing faster than the others.',
          example: 'In general, Country A consistently had the highest healthcare expenditure, while Country B showed the most rapid growth over the period.',
          tip: 'Highlight the overall ranking and the most notable change.',
        },
        {
          text: 'Table: The percentage of people using the internet was very low in all countries in 2000 but very high in all countries by 2020, though the rate of increase varied.',
          example: 'Overall, internet usage increased substantially across all countries, although the pace of growth differed significantly between them.',
          tip: 'Describe the general direction shared by all data points, then note the key difference.',
        },
      ],
    },
    {
      title: 'Complete the Task 1 Description',
      type: 'fill-in',
      instructions: 'Fill in the blanks to complete a trend description paragraph.',
      questions: [
        {
          text: 'The number of international students ___ from 50,000 in 2005 to 120,000 in 2015. (moderate upward verb)',
          answer: 'increased',
          explanation: 'A moderate, more-than-doubling over ten years is well described by "increased". "Surged" could also work but implies more speed.',
        },
        {
          text: 'After reaching a ___ of 120,000 in 2015, the figure then declined. (highest point — noun)',
          answer: 'peak',
          explanation: '"Peak" is the correct noun for the highest point in a data series.',
        },
        {
          text: 'Between 2015 and 2018, there was a ___ drop to approximately 90,000. (moderate adjective)',
          answer: 'moderate',
          explanation: 'A drop from 120,000 to 90,000 (25%) over three years is moderate — not dramatic but not slight.',
        },
        {
          text: 'From 2018 onwards, student numbers ___ at around 90,000. (stayed the same — verb)',
          answer: 'stabilised',
          explanation: '"Stabilised" means the figure stopped changing and stayed at a constant level.',
        },
      ],
    },
  ],
  tips: [
    'Learn trend vocabulary in pairs: verb + noun (e.g. "rise" → "a rise", "decline" → "a decline")',
    'Always match your language to the actual degree of change shown in the data',
    'Include an overview paragraph — it is worth more than describing every single data point',
    'Use time expressions to anchor your descriptions: "between 2005 and 2010", "over the period", "by 2020"',
  ],
  dailyPractice: [
    'Find a chart online and write 5 sentences describing its trends using varied vocabulary',
    'Practise converting verb descriptions to noun descriptions and vice versa',
    'Write an overview paragraph for a Task 1 chart in under 3 minutes',
    'Read Task 1 model answers and highlight all trend vocabulary — check if you know synonyms for each',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Describing Trends: Precise vocabulary for upward/downward trends, adjective-adverb pairs for degree of change, stability and fluctuation language, and overview writing for IELTS Writing Task 1.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...describingTrendsData,
      title: describingTrendsData.title + ' (Foundations)',
      overview: { ...describingTrendsData.overview, duration: '15-20 minutes' },
      sections: describingTrendsData.sections.slice(0, Math.max(2, Math.ceil(describingTrendsData.sections.length / 2))),
      exercises: describingTrendsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...describingTrendsData,
      title: describingTrendsData.title + ' (Advanced)',
      overview: { ...describingTrendsData.overview, duration: '30-35 minutes' },
      sections: describingTrendsData.sections,
      exercises: describingTrendsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(describingTrendsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return describingTrendsData;
}
